import pipe from "lodash/fp/pipe";
import { VALID_STATUS, VALID_SIGNAL } from "@/constants/pubStatus";
import { getGenreByURI } from "@/constants/genres";
import { SUBJECT_ORDER } from "@/constants/subjects";
import { NewsItem, NewsMLJson, NewsMLSubject } from "./types.d";

const genreResolver = getGenreByURI();

const AFX_GUID_PATTERN = /^urn:newsml:dpa-afx\.de/;

/**
 * getSubjectByType - Given a RegExp to match the type of a subject (e.g. geoArea, Ressort)
 * and a subject array, reduce matched subjects into and empty or provided list in the format
 * { name, type, uri }.
 * @param {RegExp} typeMatch - the regular expression to match
 * @param {Array} subject - the list of subject objects in a newsItem
 * @param {Array} mappedSubjects - the list of already mapped subject objects (initially empty)
 */
export const getSubjectByType = (typeMatch: RegExp) => (subject = []) => (
  mappedSubjects = []
) =>
  subject.reduce((acc, { type, name, uri }: NewsMLSubject) => {
    if (type !== null && typeof type.match === "function") {
      if (type.match(typeMatch)) {
        return [
          ...acc,
          {
            name,
            type: type.match(typeMatch)[0].split("/")[1],
            uri
          }
        ];
      }
      return acc;
    }

    return acc;
  }, mappedSubjects);

/**
 * getOrderedSubjects - Returns an ordered list of subjects given a defined order of subject types,
 * the reducer which maps a subject by type, and the list of subjects.
 *
 * TODO: The factory should throw errors rather than setting nonsense default values.
 *
 * @param {Array<RegExp>} SUBJECT_ORDER - the list of RegExp representing the order
 * of subjects by type
 * @param {Function} reducer - returns a mapped list of subjects
 * @param {Array} subject - the subject list from a newsItem
 * @returns {Function([<Array>])} - the composed order and reduce function accepting an empty list
 */
export const getOrderedSubjects = (typesOrder: RegExp[] = SUBJECT_ORDER) => (
  reducer = getSubjectByType
  // @ts-ignore
) => subject => pipe(...typesOrder.map(type => reducer(type)(subject)));

const createNewsItem = (
  {
    guid = "",
    version_created = "", // eslint-disable-line camelcase
    headline = ["Headline"],
    genre = ["Genre"],
    embargoed = null,
    urgency = 3,
    subject = [
      {
        url: "https://wwww.example.com",
        literal: null,
        type: "/dpasubject",
        name: "Name"
      }
    ],
    keyword = ["Keyword1", "Keyword2"],
    word_count = 400, // eslint-disable-line camelcase
    description = [
      {
        text: "Description",
        role: "teaser",
        lang: null
      }
    ],
    pub_status = "/usable", // eslint-disable-line camelcase
    ednote = [
      {
        text: "Note",
        role: "EdNote",
        lang: null
      }
    ],
    signal = ["/update"],
    filterPassBadges = [],
    isFavorite = false,
    // the actual one has camel casing now, it seems
    itemClass = "",
    remoteContent = []
  }: NewsMLJson = {
    guid: "",
    version_created: "",
    headline: ["Headline"],
    genre: ["Genre"],
    embargoed: null,
    urgency: 3,
    subject: [
      {
        url: "https://wwww.example.com",
        literal: null,
        type: "/dpasubject",
        name: "Name"
      }
    ],
    keyword: ["Keyword1", "Keyword2"],
    word_count: 400, // FIXME: Can't set default word count with silent error!
    description: [
      {
        text: "Description",
        role: "teaser",
        lang: null
      }
    ],
    pub_status: "/usable",
    ednote: [
      {
        text: "Note",
        role: "EdNote",
        lang: null
      }
    ],
    signal: ["/update"],
    filterPassBadges: [],
    isFavorite: false,
    itemClass: ""
  }
): NewsItem => {
  // EC-849: NB a NewsItem is recognised from the AFX feed if the guid pattern is matched.
  // This feature will break if the GUID pattern is changed in the future.
  const isAfxItem = !!guid.match(AFX_GUID_PATTERN);
  return {
    guid,
    versionCreated: version_created // eslint-disable-line camelcase
      ? new Date(version_created)
      : new Date(),
    headline,
    genrecodes: [...genre],
    // TODO: genre will be resolved in API { uri, name }
    genre: (genre || []).map(gen => {
      if (typeof gen === "string") {
        return genreResolver(gen).name;
      }
      const { name } = gen;
      return name;
    }),
    embargoed: embargoed ? new Date(embargoed) : null,
    urgency: isAfxItem ? 3 : urgency,
    subject: getOrderedSubjects(SUBJECT_ORDER)(getSubjectByType)(subject)([]),
    keyword: keyword || [],
    wordCount: word_count,
    description:
      (description || []).reduce(
        (acc, d) => (d.text ? [...acc, d] : acc),
        []
      ) || [],
    pubStatus:
      // REVIEW: will pubs_tatus be resolved to { uri, name } ?
      // WIP: resolve http://cv.iptc.org/newscodes/pubstatusg2/usable -> usable
      pub_status && pub_status.match(VALID_STATUS) // eslint-disable-line camelcase
        ? pub_status.match(VALID_STATUS)[0].split("/")[1]
        : null,
    ednote,

    // TODO: will signal be resolved by { uri, name }?
    signal: (signal || []).map(s =>
      s.match(VALID_SIGNAL) ? s.match(VALID_SIGNAL)[0].split("/")[1] : null
    ),
    // TODO: serive will be resolved in API { uri, name }
    filterPassBadges,
    isFavorite,
    remoteContent: remoteContent.map(content => {
      return {
        colourIndicator: content.colourindicator,
        ...content
      };
    }),
    itemClass: itemClass.split("/").pop()
  };
};

export default createNewsItem;
